import { formatCurrency } from '@if/core/utils'
import ErrorIcon from '@mui/icons-material/Error'
import { Alert, Grid, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import type { FieldArrayPathByValue } from '@src/services/react-hook-form'
import { memo, useState } from 'react'
import { FieldErrors, useController, useFieldArray, type Control, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddButton from '../Common/Button/AddButton'
import DeleteIconButton from '../Common/Button/DeleteIconButton'
import EditIconButton from '../Common/Button/EditIconButton'
import ActionTableCell from '../Common/Table/ActionTableCell'
import type { PayoutLineSchemaType } from './loan-form-schema'
import PayoutLineDialog from './PayoutLineDialog'

interface PayoutLineRowProps {
  payoutLine: PayoutLineSchemaType
  errors?: FieldErrors<PayoutLineSchemaType>
  onDelete: () => void
  onUpdate: (data: PayoutLineSchemaType) => void
}

const PayoutLineRow = memo(({ payoutLine, errors, onUpdate, onDelete }: PayoutLineRowProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <TableRow>
      <TableCell>
        {t(`enum.product.${payoutLine.productId}`)}
        {errors?.productId?.message && (
          <Typography variant="inherit" color="error">
            {errors?.productId?.message}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {payoutLine.amount && formatCurrency(payoutLine.amount)}
        {errors?.amount?.message && (
          <Typography variant="inherit" color="error">
            {errors?.amount?.message}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {payoutLine.description}
        {errors?.description?.message && (
          <Typography variant="inherit" color="error">
            {errors?.description?.message}
          </Typography>
        )}
      </TableCell>
      <ActionTableCell>
        <EditIconButton onClick={() => setOpen(true)} />
        <DeleteIconButton onClick={onDelete} />
      </ActionTableCell>
      <PayoutLineDialog open={open} defaultValues={payoutLine} onClose={() => setOpen(false)} onSubmit={onUpdate} />
    </TableRow>
  )
})

PayoutLineRow.displayName = 'PayoutLineRow'

interface Props<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldArrayPathByValue<TFieldValues, PayoutLineSchemaType>
  onInsert: (data: PayoutLineSchemaType) => void
  onUpdate: (data: PayoutLineSchemaType, index: number) => void
  onDelete: (index: number) => void
}

const PayoutLineList = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  onInsert,
  onUpdate,
  onDelete,
}: Props<TFieldValues>) => {
  const { fields, remove, update, append } = useFieldArray({ name, control })
  const {
    fieldState: { error },
  } = useController({ name: name as never, control })
  const [open, setOpen] = useState(false)

  const onSubmit = (data: PayoutLineSchemaType) => {
    append(data as never)
    onInsert(data)
  }

  const onLineUpdate = (data: PayoutLineSchemaType, index: number) => {
    update(index, data as never)
    onUpdate(data, index)
  }

  const onLineRemoved = (index: number) => {
    remove(index)
    onDelete(index)
  }

  return (
    <Grid size={12}>
      {error?.message && (
        <Alert icon={<ErrorIcon fontSize="inherit" />} severity="error">
          {error?.message}
        </Alert>
      )}
      <Table>
        <TableBody>
          {fields.map((fundedProduct, index) => {
            const { id, productId, amount, description } = fundedProduct as unknown as PayoutLineSchemaType & {
              id: string
            }
            return (
              <PayoutLineRow
                key={id}
                errors={error ? (error as never)[index] : undefined}
                payoutLine={{ productId, amount, description }}
                onDelete={() => onLineRemoved(index)}
                onUpdate={(data: PayoutLineSchemaType) => onLineUpdate(data, index)}
              />
            )
          })}
        </TableBody>
      </Table>
      <Stack direction="row" alignItems="center" sx={{ justifyContent: 'flex-end' }} margin={1}>
        <AddButton variant="contained" onClick={() => setOpen(true)} />
      </Stack>
      <PayoutLineDialog open={open} onClose={() => setOpen(false)} onSubmit={onSubmit} />
    </Grid>
  )
}

export default PayoutLineList
